import React, { useEffect, useState } from 'react';
import { Typography, AppBar, Toolbar, IconButton, Tooltip, Box, useMediaQuery, useTheme, Badge, Menu, MenuItem, Button } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HomeIcon from '@mui/icons-material/Home';
import { GroupAdd, MoreVert, Support, Policy, Login as LoginIcon, Settings } from '@mui/icons-material';
import { getEventDetails } from '../actions/events';
import { getPendingRequests } from '../actions/friends';
import { useAuth0 } from '@auth0/auth0-react';
import Cookies from 'js-cookie';
import { toolbarSx } from '../utils/appLayout';

const TopBar = ({ toggleDrawer, isSidebarVisible, darkMode, setDarkMode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [eventTitle, setEventTitle] = useState('');
  const [pendingRequests, setPendingRequests] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { logout } = useAuth0();

  const section = location.pathname.match(/\/events\/\d+\/(\w+)/);
  const eventIdMatch = location.pathname.match(/\/events\/(\d+)/);
  const pathKey = section ? section[1] : null;
  const eventId = eventIdMatch ? eventIdMatch[1] : null;

  useEffect(() => {
    // Fetch event details if eventId is available
    if (eventId) {
      const fetchEventTitle = async () => {
        try {
          const event = await getEventDetails(eventId);
          setEventTitle(event.title);
        } catch (err) {
          console.error('Failed to fetch event data', err);
        }
      };
      fetchEventTitle();
    }

    // Fetch pending friend requests only if user is authenticated and has a valid token
    const token = Cookies.get('token');
    if (token) {
      const fetchPendingRequests = async () => {
        try {
          const requests = await getPendingRequests();
          setPendingRequests(requests);
        } catch (err) {
          console.error('Failed to fetch pending requests', err);
        }
      };
      fetchPendingRequests();
    } else {
      // Clear pending requests if user is not authenticated or token is missing
      setPendingRequests([]);
    }
  }, [eventId]);

  const getTitle = () => {
    const pathTitles = {
      setup: 'Planning Pulse',
      tasks: 'Task Management',
      budget: 'Budget Tracking',
      guests: 'Guest List Management',
      calendar: 'Event Calendar',
      home: 'Home',
    };

    if (location.pathname.startsWith('/events/multi-view/') && location.pathname.includes('/reports/')) {
      return 'Multi Event View - Reports';
    }

    if (location.pathname.startsWith('/events/multi-view/')) {
      return 'Multi Event View';
    }

    if (location.pathname.startsWith('/events/') && location.pathname.includes('/reports/')) {
      return `${eventTitle} - Reports`;
    }

    return eventId ? [eventTitle, pathTitles[pathKey]].filter(Boolean).join(' - ') || 'Event Planning App' : 'Event Planning App';
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
    Cookies.remove('token', { path: '/' });
    Cookies.remove('user', { path: '/' });
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const isLoggedIn = Cookies.get('token'); // Check if user is actually logged in
  const isOnPublicPage = ['/support', '/privacy'].includes(location.pathname);
  const title = getTitle();

  return (
    <AppBar
      position="fixed"
      color={theme.palette.mode === 'dark' ? 'primary' : 'default'}
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        transition: 'all 0.3s ease',
        background: theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.05)'
          : theme.palette.primary.main,
        backdropFilter: 'blur(20px)',
        borderBottom: theme.palette.mode === 'dark' 
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 20px rgba(0, 0, 0, 0.3)'
          : '0 4px 20px rgba(25, 118, 210, 0.25)',
      }}
    >
      <Toolbar
        sx={{
          ...toolbarSx,
          display: 'flex',
          justifyContent: 'space-between',
          padding: { xs: '4px 12px', md: '12px 24px' },
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            minHeight: 40,
          }}
        >
          {/* Mobile Drawer Toggle Icon */}
          {isSidebarVisible && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Toggle navigation"
              onClick={toggleDrawer}
              sx={{ 
                display: { xs: 'block', md: 'none' }, 
                color: 'rgba(255, 255, 255, 0.95)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Title */}
          <Typography
            variant="h6"
            component="div"
            noWrap
            title={title}
            sx={{
              fontSize: { xs: '1.0rem', sm: '1.1rem', md: '1.25rem' },
              fontWeight: 600,
              minWidth: 0,
              flex: 1,
              color: 'rgba(255, 255, 255, 0.98)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            }}
          >
            {(title === 'Event Planning App' && isMobile) ? '' : title}
          </Typography>
        </Box>

        {/* Icon Buttons (Group of Icons) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: 'auto',
            flexShrink: 0,
            minHeight: 40,
            gap: { xs: 0.5, sm: 1 },
          }}
        >
          {/* Show navigation buttons only when logged in */}
          {isLoggedIn && (
            <>
              {/* Home Icon for navigation */}
              <Tooltip title="Home" placement="bottom">
                <IconButton 
                  color="inherit" 
                  onClick={() => navigate('/home')} 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.15)',
                      color: 'white',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <HomeIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Friends" placement="bottom">
                <IconButton 
                  color="inherit" 
                  onClick={() => navigate('/friends')} 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.15)',
                      color: 'white',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Badge 
                    badgeContent={pendingRequests.length} 
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        background: 'linear-gradient(45deg, #ff4569, #ff6b35)',
                        boxShadow: '0 2px 8px rgba(255, 69, 105, 0.4)',
                      }
                    }}
                  >
                    <GroupAdd />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Tooltip title="Logout" placement="bottom">
                <IconButton 
                  color="inherit" 
                  onClick={handleLogout} 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      background: 'rgba(244, 67, 54, 0.2)',
                      color: '#ffcdd2',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ExitToAppIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* Show login button when on public pages and not logged in */}
          {!isLoggedIn && isOnPublicPage && (
            <Tooltip title="Login" placement="bottom">
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<LoginIcon />}
                onClick={() => navigate('/login')}
                sx={{ 
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  borderColor: 'rgba(255, 255, 255, 0.6)',
                  color: 'rgba(255, 255, 255, 0.95)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Login
              </Button>
            </Tooltip>
          )}

          {/* Dark mode toggle with tooltip */}
          <Tooltip title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'} placement="bottom">
            <IconButton
              color="inherit"
              onClick={() => setDarkMode((prev) => !prev)}
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  transform: 'rotate(180deg) scale(1.05)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>

          {/* More menu for support and privacy */}
          <Tooltip title="More options" placement="bottom">
            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <MoreVert />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiPaper-root': {
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(30, 30, 30, 0.95)'
                  : 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: 2,
                border: theme.palette.mode === 'dark' 
                  ? '1px solid rgba(255, 255, 255, 0.08)'
                  : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                  : '0 8px 32px rgba(0, 0, 0, 0.15)',
                mt: 1,
              },
              '& .MuiMenuItem-root': {
                borderRadius: 1,
                mx: 1,
                my: 0.5,
                '&:hover': {
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(25, 118, 210, 0.1)'
                    : 'rgba(25, 118, 210, 0.08)',
                },
                transition: 'all 0.2s ease',
              }
            }}
          >
            {isLoggedIn && (
              <MenuItem onClick={() => handleMenuClick('/settings')}>
                <Settings sx={{ mr: 2, color: theme.palette.primary.main }} />
                Settings
              </MenuItem>
            )}
            <MenuItem onClick={() => handleMenuClick('/support')}>
              <Support sx={{ mr: 2, color: theme.palette.primary.main }} />
              Support
            </MenuItem>
            <MenuItem onClick={() => handleMenuClick('/privacy')}>
              <Policy sx={{ mr: 2, color: theme.palette.primary.main }} />
              Privacy Policy
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
